import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

@Component({
  selector: 'app-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './create-rate-modal.component.html',
  styleUrl: './create-rate-modal.component.scss',
})
export class CreateRateModalComponent implements OnInit {
  // --- INJECTIONS ---
  private modalService = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private rateManagerService = inject(RateManagerService);
  private zoneService = inject(ZoneService);

  // --- STATE: FORMS ---
  rateForm!: FormGroup;
  isSubmitting = false;
  submitted = false;
  currentStep: number = 1;
  isSaveButtonEnabled: boolean = false;

  // --- STATE: CATEGORIES ---
  categoryTypeArray: Array<any> = [];
  selectedCategoryID: number | null = null;
  selectedCategory: string = '';

  // --- STATE: LOCATIONS (Separated Search) ---
  originArray: Array<any> = [];
  destinationArray: Array<any> = [];
  isLoadingOrigin = false;
  isLoadingDestination = false;

  originSearchTerm$ = new Subject<string>();
  destinationSearchTerm$ = new Subject<string>();

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  // --- STATE: DYNAMIC FIELDS ---
  dynamicFieldNames: string[] = [];
  dynamicFieldLabelsMap: Record<string, string> = {};
  isLoadingDynamicFields = false;

  // --- LIFECYCLE ---
  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      origin_id: ['', Validators.required],
      origin_type: ['', Validators.required],
      pickup_type: ['', Validators.required],
      destination_id: ['', Validators.required],
      destination_type: ['', Validators.required],
      delivery_type: ['', Validators.required],
      transit_days: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      dynamic_fields: this.fb.group({}),
      pallet_fields: this.fb.array([]),
      furniture_fields: this.fb.array([]),
    });

    this.setupOriginSearch();
    this.setupDestinationSearch();
    this.getCategory();
  }

  // --- SEARCH LOGIC (Separate Streams) ---
  private setupOriginSearch() {
    this.originSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingOrigin = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.originArray = data;
      this.isLoadingOrigin = false;
    });
  }

  private setupDestinationSearch() {
    this.destinationSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingDestination = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.destinationArray = data;
      this.isLoadingDestination = false;
    });
  }

  async getLocations(term: string) {
    try {
      const payload = { name: term, page_size: 50 };
      const response = await this.zoneService.getProviderZones(payload);
      return (response.result || []).map((item: any) => ({
        value: item.id,
        label: item.name,
        type: item.type || 'door'
      }));
    } catch (error) {
      return [];
    }
  }

  // --- DATA FETCHING ---
  async getCategory() {
    try {
      const response = await this.rateManagerService.getProviderRatesCategories();
      this.categoryTypeArray = response.data.map((item: any) => ({
        value: item.id,
        label: item.name ? item.name.charAt(0).toUpperCase() + item.name.slice(1) : item.name,
        description: item.description,
        meta: item.meta,
        bracket_type: item.bracket_type,
      }));
    } catch (error) {
      this.categoryTypeArray = [];
    }
  }

  // --- EVENT HANDLERS ---
  onOriginSelected(item: any) {
    if (item) this.rateForm.patchValue({ origin_type: 'zone' });
  }

  onDestinationSelected(item: any) {
    if (item) this.rateForm.patchValue({ destination_type: 'zone'});
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;
    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.label,
    });
  };

  // --- STATE: METADATA ---
  hasConfiguredRanges = false;
  configuredRanges: any[] = [];

  // --- WIZARD NAVIGATION ---
  goToPrevStep = () => {
    if (this.currentStep > 1) {
      this.resetDynamicFields();
      this.resetPalletFields();
      this.resetFurnitureFields();
      this.resetStepFields();
      this.submitted = false; // Reset validation state
      this.currentStep--;
      this.isSaveButtonEnabled = this.currentStep > 1;
    }
  };

  goToNextStep = async () => {
    this.resetDynamicFields();
    this.resetPalletFields();
    this.resetFurnitureFields();
    this.resetStepFields();
    this.hasConfiguredRanges = false;
    this.configuredRanges = [];

    if (this.currentStep === 1) {
      if (!this.selectedCategoryID) return;

      this.isLoadingDynamicFields = true;
      const initLocs = await this.getLocations('');
      this.originArray = [...initLocs];
      this.destinationArray = [...initLocs];

      if (this.selectedCategory.toLowerCase() === 'vehicle') {
        const response: any = await this.rateManagerService.getProviderRatesSubCategories(this.selectedCategoryID);
        this.createDynamicField(response.data);
      }
      if (this.selectedCategory.toLowerCase() === 'pallet') {
        this.initializePalletFields();
      }
      if (this.selectedCategory.toLowerCase() === 'furniture') {
        this.initializeFurnitureFields();
      }
      this.isLoadingDynamicFields = false;
      this.isSaveButtonEnabled = true;
    }

    this.currentStep++;
  };

  initializePalletFields() {
    const category = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID);
    const meta = category?.meta;

    let ranges: any[] = [];

    if (Array.isArray(meta?.bracket_ranges?.ranges) && meta.bracket_ranges.ranges.length > 0) {
      ranges = meta.bracket_ranges.ranges;
    } else if (meta?.settings) {
      const weightSetting = meta.settings.find((s: any) => s.key === 'weight_ranges' || s.attribute_code === 'weight_ranges');
      if (weightSetting) {
        if (Array.isArray(weightSetting.value)) {
          ranges = weightSetting.value;
        } else if (typeof weightSetting.value === 'string') {
          try {
            ranges = JSON.parse(weightSetting.value);
          } catch (e) { console.error('Failed to parse weight ranges', e); }
        }
      }
    }

    if (ranges.length > 0) {
      this.hasConfiguredRanges = true;
      this.configuredRanges = ranges;
      ranges.forEach(range => this.addPalletField(range));
    } else {
      this.hasConfiguredRanges = false;
      this.configuredRanges = [];
      this.addPalletField({ min: 0, max: 1000, unit: '' });
    }
  }

  // --- FORM HELPERS ---
  resetStepFields() {
    this.rateForm.patchValue({
      origin_id: null, origin_type: null, pickup_type: null,
      destination_id: null, destination_type: null, delivery_type: null,
      transit_days: null,
    });
    Object.values(this.rateForm.controls).forEach(control => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  resetDynamicFields() {
    this.dynamicFieldNames = [];
    this.dynamicFieldLabelsMap = {};
    this.rateForm.setControl('dynamic_fields', this.fb.group({}));
  }

  resetPalletFields() {
    (this.rateForm.get('pallet_fields') as FormArray).clear();
  }

  get dynamicFieldsGroup(): FormGroup {
    return this.rateForm.get('dynamic_fields') as FormGroup;
  }

  get palletFields(): FormGroup[] {
    return (this.rateForm.get('pallet_fields') as FormArray).controls as FormGroup[];
  }

  addPalletField(range?: { min: number, max: number, unit?: string }) {
    const palletArray = this.rateForm.get('pallet_fields') as FormArray;

    // If we have configured ranges and no specific range was passed (manual add button click),
    // we take the NEXT one from the configuration list.
    if (this.hasConfiguredRanges && !range) {
        // Validation: Don't add if we've reached the limit
        if (palletArray.length >= this.configuredRanges.length) return;

        range = this.configuredRanges[palletArray.length];
    }

    const isReadOnly = this.hasConfiguredRanges;

    const group = this.fb.group({
      range_from: [{ value: range?.min ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      range_to: [{ value: range?.max ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      bracket_rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      unit: [{ value: range?.unit ?? '', disabled: true }],
    });

    palletArray.push(group);
  }

  removePalletField(index: number) {
    const palletArray = this.rateForm.get('pallet_fields') as FormArray;
    if (palletArray.length > 1) {
      palletArray.removeAt(index);
    }
  }

  // --- FURNITURE FIELDS ---
  initializeFurnitureFields() {
    const category = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID);
    const meta = category?.meta;

    let ranges: any[] = [];

    if (Array.isArray(meta?.bracket_ranges?.ranges) && meta.bracket_ranges.ranges.length > 0) {
      ranges = meta.bracket_ranges.ranges;
    } else if (meta?.settings) {
      const weightSetting = meta.settings.find((s: any) => s.key === 'weight_ranges' || s.attribute_code === 'weight_ranges');
      if (weightSetting) {
        if (Array.isArray(weightSetting.value)) {
          ranges = weightSetting.value;
        } else if (typeof weightSetting.value === 'string') {
          try {
            ranges = JSON.parse(weightSetting.value);
          } catch (e) { console.error('Failed to parse weight ranges', e); }
        }
      }
    }

    if (ranges.length > 0) {
      this.hasConfiguredRanges = true;
      this.configuredRanges = ranges;
      ranges.forEach(range => this.addFurnitureField(range));
    } else {
      this.hasConfiguredRanges = false;
      this.configuredRanges = [];
      this.addFurnitureField({ min: 0, max: 1000, unit: '' });
    }
  }

  get furnitureFields(): FormGroup[] {
    return (this.rateForm.get('furniture_fields') as FormArray).controls as FormGroup[];
  }

  addFurnitureField(range?: { min: number, max: number, unit?: string }) {
    const furnitureArray = this.rateForm.get('furniture_fields') as FormArray;

    if (this.hasConfiguredRanges && !range) {
      if (furnitureArray.length >= this.configuredRanges.length) return;
      range = this.configuredRanges[furnitureArray.length];
    }

    const isReadOnly = this.hasConfiguredRanges;

    const group = this.fb.group({
      range_from: [{ value: range?.min ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      range_to: [{ value: range?.max ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      bracket_rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      unit: [{ value: range?.unit ?? '', disabled: true }],
    });

    furnitureArray.push(group);
  }

  removeFurnitureField(index: number) {
    const furnitureArray = this.rateForm.get('furniture_fields') as FormArray;
    if (furnitureArray.length > 1) {
      furnitureArray.removeAt(index);
    }
  }

  resetFurnitureFields() {
    (this.rateForm.get('furniture_fields') as FormArray).clear();
  }

  createDynamicField = (data: any[]) => {
    if (!data || data.length === 0) return;
    const group = this.fb.group({});
    this.dynamicFieldNames = [];
    data.forEach((item) => {
      const idStr = item.id.toString();
      this.dynamicFieldNames.push(idStr);
      this.dynamicFieldLabelsMap[idStr] = this.convertToHuman(item.name);
      group.addControl(idStr, this.fb.control(null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]));
    });
    this.rateForm.setControl('dynamic_fields', group);
  };

  // --- SUBMISSION ---
  handleSubmitRate = () => {
    this.submitted = true;
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const category = this.selectedCategory.toLowerCase();
    if (category === 'pallet') this.handleCreatePalletRate();
    if (category === 'furniture') this.handleCreateFurnitureRate();
    if (category === 'vehicle' || category === 'car') this.handleCreateCarRate();
  };

  handleCreateCarRate() {
    const formData = this.rateForm.getRawValue();
    const dynamicFields = formData.dynamic_fields || {};
    const rates = Object.keys(dynamicFields).map(id => ({
      rate: dynamicFields[id]?.toString() || '0',
      rate_type_id: +id,
    }));
    this.rateManagerService.createRate({ ...formData, rates }).subscribe({
      next: () => this.handleSuccess(),
      error: () => (this.isSubmitting = false)
    });
  }

  handleCreatePalletRate() {
    const formData = this.rateForm.getRawValue();
    const bracket_type = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID)?.bracket_type;
    const rateBrackets = formData.pallet_fields.map((field: any, index: number) => ({
      bracket_order: index + 1,
      range_from: +field.range_from,
      range_to: +field.range_to,
      bracket_rate: parseFloat(field.bracket_rate),
      bracket_type,
    }));
    this.rateManagerService.createRate({ ...formData, rate_brackets: rateBrackets }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  handleCreateFurnitureRate() {
    const formData = this.rateForm.getRawValue();
    const bracket_type = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID)?.bracket_type;
    const rateBrackets = formData.furniture_fields.map((field: any, index: number) => ({
      bracket_order: index + 1,
      range_from: +field.range_from,
      range_to: +field.range_to,
      bracket_rate: parseFloat(field.bracket_rate),
      bracket_type,
    }));
    this.rateManagerService.createRate({ ...formData, rate_brackets: rateBrackets }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  private handleSuccess() {
    AlertUtil.success('Rate created successfully.');
    this.closeModal('success');
    this.isSubmitting = false;
  }

  // --- UTIL ---
  convertToHuman = (text: string) => text.trim().replace(/\s+/g, ' ').split(/[_\s]/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  closeModal = (result?: string) => {
    this.submitted = false; // Reset validation state
    this.modalService.close(result);
  };

  cancelModal() {
    this.submitted = false; // Reset validation state
    this.modalService.dismiss();
  }

  allowNumbersWithDecimal(event: KeyboardEvent, field: AbstractControl, allowDecimal: boolean) {
    const input = field.value?.toString() || '';
    const char = event.key;
    if (!/[0-9.]/.test(char) || (char === '.' && (!allowDecimal || input.includes('.')))) {
      event.preventDefault();
    }
  }
}
